import './membership-banner.component.scss';

import type { FunctionComponent } from 'preact';
import { memo } from 'preact/compat';
import { useMemo } from 'preact/hooks';

import type { AccountSettings } from '../../../models/common.model';

interface MembershipBannerProps {
  accountSettings: AccountSettings;
  header?: string;
  headerSize?: string;
  subheader?: string;
  showBanner?: boolean;
  showBannerImage?: boolean;
  showHeader?: boolean;
  showSubheader?: boolean;
}

const MembershipBannerComponent: FunctionComponent<MembershipBannerProps> = ({
  accountSettings,
  header: headerProp,
  headerSize,
  subheader: subheaderProp,
  showBanner,
  showBannerImage = true,
  showHeader = true,
  showSubheader = true,
}) => {
  const settings = useMemo(() => {
    // Determine if we should show settings
    if (showBanner || accountSettings?.showBanner) {
      return accountSettings;
    }
    return null;
  }, [accountSettings, showBanner]);

  const header = useMemo(() => {
    if (headerProp) return headerProp;
    if (settings?.header) return settings.header;
    return '';
  }, [headerProp, settings?.header]);

  const subheader = useMemo(() => {
    if (subheaderProp) return subheaderProp;
    if (settings?.subheader) return settings.subheader;
    return '';
  }, [subheaderProp, settings?.subheader]);

  const bannerImageStyle = useMemo(
    () => ({
      backgroundImage: settings?.bannerImageUrl ? `url(${settings.bannerImageUrl})` : undefined,
    }),
    [settings?.bannerImageUrl]
  );

  const hasContent =
    (showBannerImage && !!settings?.bannerImageUrl) ||
    (showHeader && !!header) ||
    (showSubheader && !!subheader);

  if (!settings || !hasContent) {
    return null;
  }

  return (
    <div className="single-membership-banner">
      {/* Banner Image */}
      {showBannerImage && settings.bannerImageUrl && (
        <div className="single-membership-banner-image" style={bannerImageStyle} />
      )}
      {/* Header */}
      {header && header !== '' && showHeader && (
        <div className="single-membership-header">
          <h1 className={headerSize}>
            <span>{header}</span>
          </h1>
        </div>
      )}
      {/* Subheader with HTML */}
      {subheader && subheader !== '' && showSubheader && (
        <p
          className="single-membership-subheader"
          dangerouslySetInnerHTML={{ __html: subheader }}
        />
      )}
    </div>
  );
};

// Memoize component to prevent unnecessary re-renders
export const MembershipBanner = memo(MembershipBannerComponent);
